import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import { flattenedDataToEnrichedCanonical } from 'services/datachecks/flattenedDataToEnrichedCanonical';
import { useExecuteCapability } from 'services/repositories/capabilities/capabilities';
import { usePackingListStore } from 'stores/packingList/packingList';
import { useWebEDIStore } from 'stores/webedi/webedi';

/**
 * Requests fresh SSCC `unit_identifier` values from the backend (POST /v2/capabilities) for the
 * `count` transport units the packing list still needs after client-side reuse.
 *
 * The request resolves against the in-progress canonical draft, built from the parent form's whole
 * document (`getValues()`) — not the transport-units DataTable context, which only holds that one
 * section. Relationship and document type come from the packingList store (synced by useCapabilities).
 * Returns `[]` for a non-positive count, and throws if the backend returns fewer identifiers than
 * requested so the caller never applies units without a valid identifier.
 */
export const useGenerateUnitIdentifiers = () => {
  const { getValues } = useFormContext();
  const { mutateAsync: executeCapability } = useExecuteCapability();

  return useCallback(
    async (count: number): Promise<Array<string>> => {
      if (count <= 0) {
        return [];
      }

      const { relationshipId, documentType } = usePackingListStore.getState();
      const { processSpecificationFields } = useWebEDIStore.getState();
      if (!relationshipId || !documentType || !processSpecificationFields) {
        throw new Error(
          'Cannot generate unit identifiers: missing relationship, document type, or process specification',
        );
      }

      const canonical = (await flattenedDataToEnrichedCanonical(
        processSpecificationFields,
        documentType,
        getValues(),
      )) as Record<string, unknown>;

      const { ssccs } = await executeCapability({
        relationship_id: relationshipId,
        canonical,
        capability: 'packing_list',
        arguments: { count },
      });

      if (ssccs.length < count) {
        throw new Error(`Requested ${count} unit identifiers but the backend returned ${ssccs.length}`);
      }

      return ssccs;
    },
    [getValues, executeCapability],
  );
};
